import { QuizDiagnosticsBookGroup, QuizDiagnosticsChapter } from '../types'

export const MAX_BOOK_COLUMNS_PER_ROW = 13
export const ALL_SUMMARY_COLUMNS = 1
export const ALL_SUMMARY_WIDTH_PERCENT = 12
export const EXAMPLE_LEGEND_WIDTH_PERCENT = 28
export const FIRST_ROW_BOOKS_WIDTH_PERCENT =
  100 - ALL_SUMMARY_WIDTH_PERCENT - EXAMPLE_LEGEND_WIDTH_PERCENT
export const MAX_BOOK_CARDS_ON_FIRST_ROW = 1

export type BookGroupRowSegment = {
  book: QuizDiagnosticsBookGroup
  chapters: QuizDiagnosticsChapter[]
  columnSpan: number
  isContinuation?: boolean
}

export type BookGroupRow = {
  segments: BookGroupRowSegment[]
  includesAllSummary: boolean
}

const getRemainingColumns = (
  usedColumns: number,
  includesAllSummary: boolean
): number => {
  const reserved = includesAllSummary ? ALL_SUMMARY_COLUMNS : 0

  return MAX_BOOK_COLUMNS_PER_ROW - reserved - usedColumns
}

export const packBookGroupsIntoRows = (
  bookGroups: QuizDiagnosticsBookGroup[]
): BookGroupRow[] => {
  const rows: BookGroupRow[] = []
  let currentSegments: BookGroupRowSegment[] = []
  let usedColumns = 0
  let isFirstRow = true

  const flushRow = () => {
    if (currentSegments.length === 0 && !isFirstRow) {
      return
    }

    rows.push({
      segments: currentSegments,
      includesAllSummary: isFirstRow
    })

    currentSegments = []
    usedColumns = 0
    isFirstRow = false
  }

  bookGroups.forEach(book => {
    let remainingChapters = book.chapters ?? []
    let isFirstSegmentForBook = true

    while (remainingChapters.length > 0) {
      const includesAllSummary = isFirstRow
      const lastSegment = currentSegments.at(-1)
      const isContinuingSameBook = lastSegment?.book.bookId === book.bookId

      if (
        includesAllSummary &&
        currentSegments.length >= MAX_BOOK_CARDS_ON_FIRST_ROW
      ) {
        flushRow()
        continue
      }

      const remainingColumns = getRemainingColumns(
        usedColumns,
        includesAllSummary
      )

      if (remainingColumns <= 0) {
        flushRow()
        continue
      }

      if (
        !isContinuingSameBook &&
        remainingChapters.length > remainingColumns &&
        remainingChapters.length <= MAX_BOOK_COLUMNS_PER_ROW &&
        currentSegments.length > 0
      ) {
        flushRow()
        continue
      }

      const take = Math.min(remainingChapters.length, remainingColumns)
      const chaptersForSegment = remainingChapters.slice(0, take)

      currentSegments.push({
        book,
        chapters: chaptersForSegment,
        columnSpan: chaptersForSegment.length,
        isContinuation: !isFirstSegmentForBook
      })
      isFirstSegmentForBook = false

      usedColumns += chaptersForSegment.length
      remainingChapters = remainingChapters.slice(take)

      if (getRemainingColumns(usedColumns, includesAllSummary) <= 0) {
        flushRow()
      }
    }
  })

  if (currentSegments.length > 0 || isFirstRow) {
    flushRow()
  }

  return rows
}

export const getRowCardCount = (row: BookGroupRow): number =>
  row.segments.length + (row.includesAllSummary ? 1 : 0)

export const getRowChapterCount = (row: BookGroupRow): number =>
  row.segments.reduce((total, segment) => total + segment.columnSpan, 0)

export const getRowMaxColumns = (row: BookGroupRow): number => {
  const cardCount = getRowCardCount(row)
  const chapterCount = getRowChapterCount(row)
  const reservedColumns = row.includesAllSummary ? ALL_SUMMARY_COLUMNS : 0
  const rowCapacity = Math.max(14 - cardCount, 1)

  return Math.max(rowCapacity, chapterCount + reservedColumns)
}

export const getRowTotalColumns = (row: BookGroupRow): number => {
  const summaryColumns = row.includesAllSummary ? ALL_SUMMARY_COLUMNS : 0
  const segmentColumns = getRowChapterCount(row)

  return summaryColumns + segmentColumns
}

export const getAllSummaryFlexBasis = (): string =>
  `${ALL_SUMMARY_WIDTH_PERCENT}%`

export const getSegmentFlexBasis = (
  row: BookGroupRow,
  segmentColumnSpan: number
): string => {
  const rowMaxColumns = getRowMaxColumns(row)

  if (rowMaxColumns <= 0) {
    return '100%'
  }

  if (row.includesAllSummary) {
    const totalBookChapters = getRowChapterCount(row)

    if (totalBookChapters <= 0) {
      return `${FIRST_ROW_BOOKS_WIDTH_PERCENT}%`
    }

    const widthPercent =
      (segmentColumnSpan / totalBookChapters) * FIRST_ROW_BOOKS_WIDTH_PERCENT

    return `${widthPercent}%`
  }

  const widthPercent = (segmentColumnSpan / rowMaxColumns) * 100

  return `${widthPercent}%`
}
